'use client';

import { Box, Button, CircularProgress, Typography } from '@mui/material';

import { MarketCardList } from '@/components/specific/pages/home';
import type { EventResponse } from '@/types';

import type { BrowseFilter } from '@/constants';

interface FilteredMarketsProps {
  filter: BrowseFilter;
  events: EventResponse[];
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  loadMoreRef: (node?: Element | null) => void;
  onClearFilter: () => void;
}

export const FilteredMarkets = ({
  filter,
  events,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  loadMoreRef,
  onClearFilter,
}: FilteredMarketsProps) => {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (events.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No markets found
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
          {filter.label}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={onClearFilter}
          sx={{ textTransform: 'none', fontSize: '0.875rem' }}
        >
          Clear Filter
        </Button>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: 2,
        }}
      >
        <MarketCardList
          events={events}
          onTradeClick={() => {}}
          isResolvedFilter={filter.label.toLowerCase() === 'resolved'}
        />
      </Box>

      {hasNextPage && (
        <Box
          ref={loadMoreRef}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 4,
            mb: 2,
            width: '100%',
            py: 2,
          }}
        >
          {isFetchingNextPage && <CircularProgress size={30} />}
        </Box>
      )}
    </>
  );
};
